import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidateCustomer, useGetCustomerNamesWithProjectPlanningAccess } from 'src/api/customer'
import { invalidateCustomerCache, useAddCustomer } from 'src/api/customer-admin'
import { ICustomer } from 'src/generated-types'
import { ShowDialogProps } from 'src/show-dialog'
import CustomerForm from './CustomerForm'
import { Box, FormControl, FormControlLabel, Radio, RadioGroup } from '@mui/material'
import { useEffect, useState } from 'react'
import { CancelButton } from 'src/components/CancelButton'
import { MainButton } from 'src/components/MainButton'
import { AppForm, FormSelectField } from 'src/components/FormCore'
import { useZodForm } from 'src/hooks/zod-form'
import { z } from 'zod'
import { CreateCustomerOption } from 'src/enums'
import { CenteredSpinner } from 'src/components/Spinner'
import FormStepper from '../components/component/FormStepper'

interface CustomerDialogProps extends ShowDialogProps {
  withSteps?: boolean
  steps?: [{ label: string; completed: boolean; active: boolean }]
  onStepSuccess?: (customerId: number) => void
  customerId?: number
}

const CustomerDialog: React.FC<CustomerDialogProps> = ({
  pop,
  withSteps = false,
  steps,
  onStepSuccess,
  customerId,
}) => {
  const { mutateAsync } = useAddCustomer()
  const { t: translate } = useTranslation(['admin-customer', 'common'])
  const [selectedValue, setSelectedValue] = useState<CreateCustomerOption>(CreateCustomerOption.PickExistingCustomer)

  const { data: customers, isLoading } = useGetCustomerNamesWithProjectPlanningAccess()

  const onSubmit = async (form: ICustomer) => {
    await mutateAsync(form, {
      onSuccess: res => {
        invalidateCustomer.getUniqueAdminCustomers()
        invalidateCustomer.getCustomersNameWithProjectPlanningAccess()
        invalidateCustomerCache.getCustomers()
        toast.success(translate('addedSuccessfully', { ns: 'common' }))
        pop(true)
        onStepSuccess && onStepSuccess(res.data.id)
      },
      onError: () => {
        toast.error(translate('addedFailed', { ns: 'common' }))
      },
    })
  }

  const customerSchema = z.object({
    customerId: z.number().min(1, translate('customer-required', { ns: 'projects' })),
    customer: z.any().optional(),
  })

  const form = useZodForm(customerSchema, {})

  const handleChange = (key, value) => {
    form.setValue(key, value)
    form.setValue(`${key}Id` as 'customerId', value?.id)
  }

  const onPickCustomer = form => {
    onStepSuccess && onStepSuccess(form.customerId)
    pop()
  }

  useEffect(() => {
    if (customers && customerId) {
      setSelectedValue(CreateCustomerOption.PickExistingCustomer)
      const selectedCustomer = customers?.find(c => c.id === customerId)
      if (selectedCustomer) {
        form.setValue('customerId', customerId)
        form.setValue('customer', selectedCustomer)
      }
    }
  }, [customerId, customers, form])

  return (
    <>
      {withSteps && steps && <FormStepper steps={steps} />}
      <Box p={1}>
        <FormControl>
          <RadioGroup
            aria-labelledby="demo-radio-buttons-group-label"
            defaultValue={CreateCustomerOption.PickExistingCustomer}
            name="radio-buttons-customer"
            row
            value={selectedValue}
            onChange={e => setSelectedValue(e.target.value as CreateCustomerOption)}
          >
            {Object.values(CreateCustomerOption).map(option => (
              <FormControlLabel
                key={option}
                value={option}
                control={<Radio />}
                label={translate(option, { ns: 'admin-customer' }) as string}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Box>
      {selectedValue === CreateCustomerOption.CreateNewCustomer ? (
        <CustomerForm onSubmit={onSubmit} onCancel={() => pop()} />
      ) : (
        <Box>
          <AppForm form={form}>
            {isLoading ? (
              <CenteredSpinner height={20} />
            ) : (
              <FormSelectField
                name="customer"
                label={translate('customer', { ns: 'projects' })}
                data={customers ?? []}
                getOptionLabel={option => option.name}
                renderOption={(props: any, option) => (
                  <Box key={option.id} {...props}>
                    <span style={{ textTransform: 'capitalize' }}>{option.name}</span>
                  </Box>
                )}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                onChange={(_, v) => handleChange('customer', v)}
                placeholder={translate('pick-existing-customer', { ns: 'admin-customer' }) as string}
                defaultValue={customers?.find(c => c.id === customerId)}
              />
            )}
          </AppForm>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Box display={'flex'} gap={2}>
              <CancelButton onClick={pop}>{translate('cancel', { ns: 'common' })}</CancelButton>
              <MainButton
                key="project-form-button"
                variant="contained"
                onClick={() => {
                  form.handleSubmit(onPickCustomer)()
                }}
                disabled={!form.watch('customerId')}
              >
                {translate('select-customer', { ns: 'admin-customer' })}
              </MainButton>
            </Box>
          </Box>
        </Box>
      )}
    </>
  )
}

export default CustomerDialog
